import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLines } from "../Reveal";

const ease = [0.22, 1, 0.36, 1];

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div onMouseMove={onMove} onMouseLeave={reset} className="[perspective:1200px]" data-testid="hero-tilt-card">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.4, ease, delay: 0.5 }}
          className="relative aspect-[4/5] overflow-hidden rounded-[28px] shadow-[0_40px_120px_-30px_rgba(91,155,255,0.4)]"
        >
          <motion.img
            initial={{ scale: 1.35 }}
            animate={{ scale: 1.05 }}
            transition={{ duration: 2.2, ease, delay: 0.5 }}
            src="/images/hero-jeremy.jpg"
            alt="Jeremy Spear at his desk"
            className="h-full w-full object-cover object-[50%_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-bone" style={{ transform: "translateZ(40px)" }}>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/85">Jeremy Spear</span>
            <span className="font-serif text-2xl italic">at work</span>
          </div>
        </motion.div>
        <div
          className="pointer-events-none absolute -left-6 -top-6 hidden h-24 w-24 items-center justify-center rounded-full bg-ember font-display text-sm font-extrabold uppercase text-ink sm:flex spin-slow"
          style={{ transform: "translateZ(80px)" }}
        >
          <span className="text-center leading-tight">05<br />ventures</span>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yName = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const yCard = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative min-h-[100svh] overflow-hidden bg-ink px-5 pb-14 pt-32 text-bone md:px-10 md:pt-36">
      <div className="pointer-events-none absolute -right-40 top-1/3 h-[520px] w-[520px] rounded-full bg-ember/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1500px] items-end gap-12 lg:grid-cols-12">
        <motion.div style={{ y: yName }} className="lg:col-span-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="eyebrow flex items-center gap-3 text-bone/75">
            <span className="h-px w-10 bg-ember" /> Serial Entrepreneur
          </motion.p>
          <h1 data-testid="hero-name" className="mt-8 font-display font-extrabold uppercase leading-[0.82] tracking-[-0.03em] text-[16.5vw] sm:text-[15vw] lg:text-[10vw] xl:text-[9.5rem]">
            <MaskLines onLoad lines={["Jeremy", <span key="s" className="text-stroke">Spear</span>]} />
          </h1>
          <div className="mt-10 max-w-xl">
            <MaskLines
              onLoad
              delay={3}
              className="font-serif text-2xl italic leading-snug text-bone/90 sm:text-3xl"
              lines={["I build companies that solve real problems —", <span key="c" className="text-ember">and causes that give back.</span>]}
            />
          </div>
        </motion.div>
        <motion.div style={{ y: yCard }} className="mx-auto w-full max-w-[420px] lg:col-span-4 lg:max-w-none">
          <TiltCard />
        </motion.div>
      </div>
      <motion.a
        href="#ventures"
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative mx-auto mt-14 flex max-w-[1500px] items-center gap-3 font-mono text-[12px] uppercase tracking-[0.2em] text-bone/70 hover:text-ember"
      >
        <ArrowDown size={14} className="animate-bounce" /> Explore the ventures
      </motion.a>
    </section>
  );
};
